import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const styles = `
.nature-tree-animation {
    position: fixed;
    right: -28px;
    bottom: -34px;
    width: 280px;
    height: 450px;
    z-index: 30;
    pointer-events: none;
    overflow: visible;
    transform: translateY(var(--tree-lift, 0px)) scale(var(--tree-scale, 1));
    opacity: var(--tree-opacity, 1);
    transition: opacity 0.2s ease;
}

.nature-tree-animation__trees {
    position: absolute;
    inset: 0;
}

.nature-tree-animation__tree {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform-origin: bottom right;
    animation: natureTreeSway 5.8s ease-in-out infinite;
}

.nature-tree-animation__tree--small {
    left: 50px;
    bottom: 0;
    width: 210px;
    height: 480px;
    opacity: 0.98;
    z-index: 1;
    animation-duration: 6.3s;
    transform-origin: bottom right;
}

.nature-tree-animation__tree--main {
    z-index: 2;
}

.nature-tree-animation__grow {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform-origin: bottom right;
}

.nature-tree-animation--growing .nature-tree-animation__grow {
    animation: natureTreeGrow 1.35s cubic-bezier(0.2, 0.7, 0.15, 1) both;
}

.nature-tree-animation__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: right bottom;
    filter: drop-shadow(0 16px 18px rgba(43, 66, 20, 0.12));
}

.nature-tree-animation__tree--small .nature-tree-animation__image {
    filter: drop-shadow(0 12px 14px rgba(43, 66, 20, 0.1));
}

.nature-tree-animation__falling-coconut {
    position: absolute;
    left: 142px;
    top: 176px;
    width: 34px;
    height: 40px;
    z-index: 12;
    border-radius: 46% 54% 48% 52%;
    background: radial-gradient(circle at 32% 23%, #ddb071 0, #9c6333 23%, #613c1e 62%, #332013 100%);
    box-shadow:
        inset -6px -8px 9px rgba(30, 14, 5, 0.38),
        4px 7px 10px rgba(38, 35, 16, 0.28);
    transform-origin: center;
    animation: natureCoconutFall 2.8s cubic-bezier(0.4, 0, 0.25, 1) forwards;
}

.nature-tree-animation__falling-coconut::before {
    content: "";
    position: absolute;
    left: 8px;
    top: 5px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(52, 28, 13, 0.72);
    box-shadow:
        9px 1px 0 rgba(52, 28, 13, 0.62),
        4px 7px 0 rgba(52, 28, 13, 0.55);
}

.nature-tree-animation__falling-coconut::after {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: inherit;
    border: 1px solid rgba(247, 208, 145, 0.22);
}

.nature-tree-animation__shadow {
    position: absolute;
    left: 132px;
    bottom: 34px;
    width: 58px;
    height: 13px;
    z-index: 6;
    border-radius: 50%;
    background: rgba(37, 33, 21, 0.28);
    filter: blur(3px);
    animation: natureCoconutShadow 2.8s ease-in-out forwards;
}

.nature-tree-animation__dust {
    position: absolute;
    left: 143px;
    bottom: 31px;
    width: 14px;
    height: 14px;
    z-index: 8;
    border-radius: 50%;
    background: rgba(172, 139, 86, 0.55);
    filter: blur(1px);
    animation: natureDustBurst 1.1s ease-out forwards;
}

.nature-tree-animation__dust::before,
.nature-tree-animation__dust::after {
    content: "";
    position: absolute;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: rgba(172, 139, 86, 0.45);
}

.nature-tree-animation__dust::before {
    left: -14px;
    top: 3px;
}

.nature-tree-animation__dust::after {
    right: -14px;
    top: 1px;
}

.nature-tree-animation__particle {
    position: absolute;
    left: 138px;
    top: 170px;
    width: 7px;
    height: 4px;
    border-radius: 100% 0 100% 0;
    background: #7eaa46;
    opacity: 0;
    animation: natureFloatingLeaf 4.5s linear forwards;
}

@keyframes natureTreeSway {
    0%, 100% {
        transform: rotate(-2deg) translateX(0);
    }

    50% {
        transform: rotate(1.4deg) translateX(1px);
    }
}

@keyframes natureTreeGrow {
    0% {
        transform: translateY(82px) scale(0.72);
        opacity: 0;
    }

    55% {
        opacity: 1;
    }

    100% {
        transform: translateY(0) scale(1);
        opacity: 1;
    }
}

@keyframes natureCoconutFall {
    0% {
        transform: translate(0, 0) rotate(0deg);
        opacity: 1;
    }

    12% {
        transform: translate(8px, 25px) rotate(40deg);
    }

    42% {
        transform: translate(24px, 250px) rotate(300deg);
    }

    61% {
        transform: translate(32px, 320px) rotate(500deg);
    }

    68% {
        transform: translate(36px, 280px) rotate(560deg);
    }

    78% {
        transform: translate(43px, 322px) rotate(640deg);
    }

    84% {
        transform: translate(48px, 303px) rotate(685deg);
    }

    92% {
        transform: translate(58px, 324px) rotate(730deg);
    }

    100% {
        transform: translate(76px, 326px) rotate(780deg);
        opacity: 0;
    }
}

@keyframes natureCoconutShadow {
    0% {
        transform: scale(0.25);
        opacity: 0.08;
    }

    60% {
        transform: scale(1);
        opacity: 0.34;
    }

    100% {
        transform: scale(1.4);
        opacity: 0;
    }
}

@keyframes natureDustBurst {
    0% {
        transform: scale(0.2);
        opacity: 0;
    }

    25% {
        transform: scale(1.8) translateY(-4px);
        opacity: 0.75;
    }

    100% {
        transform: scale(4) translateY(-17px);
        opacity: 0;
    }
}

@keyframes natureFloatingLeaf {
    0% {
        transform: translate(0, 0) rotate(0deg);
        opacity: 0;
    }

    15% {
        opacity: 0.8;
    }

    100% {
        transform: translate(115px, 230px) rotate(440deg);
        opacity: 0;
    }
}

@media (max-width: 767px) {
    .nature-tree-animation {
        bottom: -22px;
        right: -14px;
        width: 160px;
        height: 320px;
        --tree-scale: 0.64;
        transform-origin: right bottom;
    }

    .nature-tree-animation__tree--small {
        left: 50px;
        bottom: 0;
        width: 154px;
        height: 480px;
    }
}

@media (max-width: 420px) {
    .nature-tree-animation {
        bottom: -18px;
        right: -10px;
        --tree-scale: 0.5;
        transform-origin: right bottom;
    }

    .nature-tree-animation__tree--small {
        left: 50px;
        bottom: 0;
        width: 130px;
        height: 480px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .nature-tree-animation__tree,
    .nature-tree-animation__grow,
    .nature-tree-animation__image {
        animation: none !important;
    }

    .nature-tree-animation__falling-coconut,
    .nature-tree-animation__shadow,
    .nature-tree-animation__dust,
    .nature-tree-animation__particle {
        display: none !important;
    }
}
`;

function Tree() {
    const fallingContainerRef = useRef(null);
    const particleContainerRef = useRef(null);
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(false);
    const [treeLift, setTreeLift] = useState(0);
    const [treeOpacity, setTreeOpacity] = useState(0);
    const [isGrowing, setIsGrowing] = useState(false);
    const growTimerRef = useRef(null);

    useEffect(() => {
        const updateVisibility = () => {
            const banner = document.querySelector('.ps-header__video-banner');

            if (location.pathname !== '/') {
                setIsVisible(false);
                setTreeLift(0);
                setTreeOpacity(0);
                setIsGrowing(false);
                return;
            }

            if (!banner) {
                setIsVisible(false);
                setTreeLift(0);
                setTreeOpacity(0);
                setIsGrowing(false);
                return;
            }

            const rect = banner.getBoundingClientRect();
            const halfScrolled = rect.top <= -(rect.height * 0.5);
            const extraScroll = Math.max(0, (-rect.top) - (rect.height * 0.5));
            const liftProgress = Math.min(extraScroll / 260, 1);

            setIsVisible(halfScrolled);
            setTreeLift(-18 * liftProgress);
            setTreeOpacity(halfScrolled ? Math.max(0.25, 1 - liftProgress * 0.18) : 0);

            if (halfScrolled) {
                setIsGrowing(true);
                if (growTimerRef.current) {
                    window.clearTimeout(growTimerRef.current);
                }
                growTimerRef.current = window.setTimeout(() => {
                    setIsGrowing(false);
                    growTimerRef.current = null;
                }, 1400);
            } else {
                setIsGrowing(false);
            }
        };

        updateVisibility();
        window.addEventListener('scroll', updateVisibility, {passive: true});
        window.addEventListener('resize', updateVisibility);

        return () => {
            window.removeEventListener('scroll', updateVisibility);
            window.removeEventListener('resize', updateVisibility);
            if (growTimerRef.current) {
                window.clearTimeout(growTimerRef.current);
                growTimerRef.current = null;
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        const fallingContainer = fallingContainerRef.current;
        const particleContainer = particleContainerRef.current;

        if (!fallingContainer || !particleContainer || !isVisible) {
            return undefined;
        }

        const reducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        if (reducedMotion) {
            return undefined;
        }

        const timerIds = new Set();
        let coconutIsFalling = false;

        const schedule = (callback, delay) => {
            const id = window.setTimeout(() => {
                timerIds.delete(id);
                callback();
            }, delay);

            timerIds.add(id);
            return id;
        };

        const createDustEffect = () => {
            const dust = document.createElement('span');
            dust.className = 'nature-tree-animation__dust';
            fallingContainer.appendChild(dust);

            schedule(() => {
                dust.remove();
            }, 1200);
        };

        const createFallingCoconut = () => {
            if (coconutIsFalling || document.hidden) {
                return;
            }

            coconutIsFalling = true;

            const coconut = document.createElement('span');
            const shadow = document.createElement('span');
            coconut.className = 'nature-tree-animation__falling-coconut';
            shadow.className = 'nature-tree-animation__shadow';

            const randomStart = Math.floor(Math.random() * 14) - 7;
            coconut.style.left = `${142 + randomStart}px`;

            fallingContainer.appendChild(shadow);
            fallingContainer.appendChild(coconut);

            schedule(() => {
                createDustEffect();
            }, 1750);

            schedule(() => {
                coconut.remove();
                shadow.remove();
                coconutIsFalling = false;
            }, 2900);
        };

        const createFloatingLeaf = () => {
            if (document.hidden) {
                return;
            }

            const particle = document.createElement('span');
            particle.className = 'nature-tree-animation__particle';

            const randomLeft = 75 + Math.floor(Math.random() * 65);
            const randomTop = 80 + Math.floor(Math.random() * 80);
            const randomSize = 4 + Math.floor(Math.random() * 5);
            const randomDuration = 3.5 + Math.random() * 2.5;

            particle.style.left = `${randomLeft}px`;
            particle.style.top = `${randomTop}px`;
            particle.style.width = `${randomSize}px`;
            particle.style.height = `${Math.max(3, randomSize - 2)}px`;
            particle.style.animationDuration = `${randomDuration}s`;

            particleContainer.appendChild(particle);

            schedule(() => {
                particle.remove();
            }, randomDuration * 1000);
        };

        const scheduleNextCoconut = () => {
            const delay = 7000 + Math.random() * 7000;

            schedule(() => {
                createFallingCoconut();
                scheduleNextCoconut();
            }, delay);
        };

        const scheduleFloatingLeaves = () => {
            const delay = 1800 + Math.random() * 2500;

            schedule(() => {
                createFloatingLeaf();
                scheduleFloatingLeaves();
            }, delay);
        };

        scheduleNextCoconut();
        scheduleFloatingLeaves();

        return () => {
            timerIds.forEach((timerId) => window.clearTimeout(timerId));
            timerIds.clear();
        };
    }, [isVisible]);

    if (!isVisible) {
        return null;
    }

    return (
        <>
            <style>{styles}</style>
            <div
                className={`nature-tree-animation ${isGrowing ? 'nature-tree-animation--growing' : ''}`}
                aria-hidden="true"
                style={{
                    '--tree-lift': `${treeLift}px`,
                    '--tree-opacity': treeOpacity,
                }}
            >
                <div className="nature-tree-animation__trees">
                    <div className="nature-tree-animation__tree nature-tree-animation__tree--small">
                        <div className="nature-tree-animation__grow">
                            <img
                                className="nature-tree-animation__image"
                                src="/assets/img/palm-transparent.png"
                                alt=""
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                    <div className="nature-tree-animation__tree nature-tree-animation__tree--main">
                        <div className="nature-tree-animation__grow">
                            <img
                                className="nature-tree-animation__image"
                                src="/assets/img/palm-transparent.png"
                                alt=""
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
                <div
                    className="nature-tree-animation__falling-container"
                    ref={fallingContainerRef}
                />
                <div
                    className="nature-tree-animation__particle-container"
                    ref={particleContainerRef}
                />
            </div>
        </>
    );
}

export default Tree;
